/**
 * Quick-add modal (móvil): carrusel horizontal de imágenes estilo Peppermayo.
 *
 * Se apoya en el flujo existente de quick-add.js: cuando el modal se abre, su
 * contenido ya fue inyectado. Aquí clonamos las <img> de los slides nativos
 * (que llevan data-color) en una tira con scroll-snap y la filtramos por el
 * color seleccionado, igual que la galería de desktop pero en horizontal.
 * Debajo de la tira va una barra negra de progreso (como la de la página de
 * producto) que se mueve mientras se desliza.
 *
 * También añade, junto al título/precio:
 *  - el botón de Wysh: copia visualmente el botón real de la tarjeta que abrió
 *    el modal y reenvía el clic al original (la lógica de guardar sigue siendo
 *    de Wysh);
 *  - las estrellas de reviews, a la derecha del precio: quick-add.js descarta
 *    el bloque de detalles en móvil (donde viven las estrellas), así que aquí
 *    las rescatamos antes de que se elimine.
 *
 * Encima del botón de Add to bag, la entrega estimada; y debajo, una fila con
 * los enlaces "Size Guide" (izquierda) y "View Full Product" (derecha).
 *
 * Además, en móvil y desktop, reapunta las estrellas a su propio símbolo (ver
 * repairStarIds) para que se dibujen sin importar qué página abrió el modal.
 *
 * En desktop, quick-add.js ya crea la fila "Size Guide | Shipping Policy" sobre
 * el botón; aquí cambiamos "Shipping Policy" por "View Full Product".
 *
 * La galería nativa solo se oculta (por CSS, clase .lov-qa-on) cuando el
 * carrusel se construyó bien; si algo falla, el modal se ve como antes.
 */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 749px)');
  var CONTENT_ID = 'quick-add-modal-content';

  function getContent() {
    return document.getElementById(CONTENT_ID);
  }

  function getSelectedColor(root) {
    var picker = root.querySelector('variant-picker');
    if (!picker) return null;
    var colorSet = picker.querySelector(
      'fieldset[data-option-name="color"], fieldset[data-option-name="colour"]'
    );
    var checked = (colorSet || picker).querySelector('input[type="radio"]:checked');
    return checked ? checked.value.trim().toLowerCase() : null;
  }

  /**
   * Barra negra: su ancho es la proporción visible de la tira y se desplaza
   * en sincronía con el scroll (mismo look que la barra de la página de producto).
   */
  function updateProgress(carousel) {
    var wrap = carousel.parentElement;
    var bar = wrap && wrap.querySelector('.lov-qa-progress');
    if (!bar) return;
    var thumb = bar.firstElementChild;

    var viewW = carousel.clientWidth;
    var totalW = carousel.scrollWidth;

    if (!viewW || totalW <= viewW + 1) {
      bar.style.visibility = 'hidden';
      return;
    }

    bar.style.visibility = 'visible';
    thumb.style.width = (viewW / totalW) * 100 + '%';
    thumb.style.transform = 'translateX(' + (carousel.scrollLeft / viewW) * 100 + '%)';
  }

  function filterCarousel(root) {
    var carousel = root.querySelector('.lov-qa-carousel');
    if (!carousel) return;

    var color = getSelectedColor(root);
    var imgs = Array.prototype.slice.call(carousel.querySelectorAll('.lov-qa-carousel__img'));
    var anyMatch =
      !!color &&
      imgs.some(function (img) {
        return img.dataset.lovColor === color;
      });

    var visible = [];
    imgs.forEach(function (img) {
      var show = anyMatch ? img.dataset.lovColor === color : true;
      img.classList.toggle('is-hidden', !show);
      if (show) visible.push(img);
    });

    // Las primeras fotos visibles cargan sin esperar al lazy-loading
    visible.slice(0, 3).forEach(function (img) {
      img.loading = 'eager';
    });

    carousel.scrollLeft = 0;
    updateProgress(carousel);
  }

  function buildCarousel(root) {
    var media = root.querySelector('.product-information__media');
    if (!media) return;

    var previous = media.querySelector('.lov-qa-wrap');
    if (previous) previous.remove();
    media.classList.remove('lov-qa-on');

    var slides = media.querySelectorAll('slideshow-slide[data-color]');
    if (!slides.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'lov-qa-wrap';

    var carousel = document.createElement('div');
    carousel.className = 'lov-qa-carousel';

    slides.forEach(function (slide) {
      var img = slide.querySelector('img');
      if (!img) return;

      var clone = img.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('ref');
      clone.removeAttribute('style');
      clone.className = 'lov-qa-carousel__img';
      clone.dataset.lovColor = (slide.dataset.color || '').trim().toLowerCase();
      clone.sizes = '45vw';
      clone.draggable = false;
      carousel.appendChild(clone);
    });

    if (!carousel.children.length) return;

    var progress = document.createElement('div');
    progress.className = 'lov-qa-progress';
    progress.innerHTML = '<div class="lov-qa-progress__thumb"></div>';

    wrap.appendChild(carousel);
    wrap.appendChild(progress);

    var ticking = false;
    carousel.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          updateProgress(carousel);
        });
      },
      { passive: true }
    );

    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        updateProgress(carousel);
      }).observe(carousel);
    }

    media.insertBefore(wrap, media.firstChild);
    media.classList.add('lov-qa-on');
    filterCarousel(root);
  }

  /**
   * Botón de Wysh: clona el botón real de la tarjeta que abrió el modal
   * (quick-add.js marca ese quick-add-component con el atributo stay-visible
   * mientras el modal está abierto) y lo coloca junto al título. Al tocar el
   * clon se dispara el clic del botón real.
   */
  var wyshObserver = null;

  function setupWysh(root) {
    var header = root.querySelector('.product-header');
    if (!header) return;

    var previous = header.querySelector('.lov-wysh-proxy');
    if (previous) previous.remove();
    if (wyshObserver) {
      wyshObserver.disconnect();
      wyshObserver = null;
    }

    var quickAdd = document.querySelector('quick-add-component[stay-visible]');
    var card = quickAdd && quickAdd.closest('product-card');
    var source = card && card.querySelector('.wysh-button');
    if (!source) return;

    var proxy = source.cloneNode(true);
    proxy.classList.add('lov-wysh-proxy');
    proxy.removeAttribute('id');

    proxy.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      source.click();
    });

    header.appendChild(proxy);

    // Mantiene el estado (guardado / no guardado) sincronizado con el original
    function sync() {
      proxy.className = source.className + ' lov-wysh-proxy';
      if (proxy.innerHTML !== source.innerHTML) proxy.innerHTML = source.innerHTML;
    }

    wyshObserver = new MutationObserver(sync);
    wyshObserver.observe(source, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
  }

  /**
   * Entrega estimada, justo encima del botón de Add to bag.
   *
   * En móvil quick-add.js descarta el bloque de detalles (donde vive la entrega
   * estimada), así que la construimos aquí con el mismo cálculo que usa el
   * snippet estimated-delivery: 5 días hábiles a partir de hoy.
   */
  var BUSINESS_DAYS_TO_ADD = 5;

  function addBusinessDays(startDate, daysToAdd) {
    var result = new Date(startDate);
    var added = 0;
    while (added < daysToAdd) {
      result.setDate(result.getDate() + 1);
      var dayOfWeek = result.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) added++;
    }
    return result;
  }

  function setupDelivery(root) {
    var previous = root.querySelector('.lov-qa-delivery');
    if (previous) previous.remove();

    var form = root.querySelector('product-form-component');
    if (!form) return;

    var deliveryDate = addBusinessDays(new Date(), BUSINESS_DAYS_TO_ADD);
    var weekday = deliveryDate.toLocaleDateString('en-US', { weekday: 'long' });
    var month = deliveryDate.toLocaleDateString('en-US', { month: 'long' });
    var dayNumber = deliveryDate.getDate();

    var el = document.createElement('div');
    el.className = 'lov-qa-delivery';

    var prefix = document.createElement('span');
    prefix.textContent = 'Estimated delivery for ';

    var date = document.createElement('span');
    date.className = 'lov-qa-delivery__date';
    date.textContent = weekday + ', ' + month + ' ' + dayNumber;

    el.appendChild(prefix);
    el.appendChild(date);

    form.insertAdjacentElement('beforebegin', el);
  }

  /**
   * Enlaces debajo del botón de Add to bag: "Size Guide" al extremo izquierdo y
   * "View Full Product" al derecho. El destino del segundo es el mismo que el del
   * título del modal (incluye la variante elegida); ese enlace lo actualiza el
   * tema al cambiar de variante, así que lo releemos al tocar.
   */
  function setupLinks(root) {
    var previous = root.querySelector('.lov-qa-links');
    if (previous) previous.remove();

    var form = root.querySelector('product-form-component');
    if (!form) return;

    var row = document.createElement('div');
    row.className = 'lov-qa-links';

    var sizeGuide = document.createElement('a');
    sizeGuide.href = '/pages/size-guide';
    sizeGuide.target = '_blank';
    sizeGuide.rel = 'noopener';
    sizeGuide.textContent = 'Size Guide';
    row.appendChild(sizeGuide);

    var titleLink = root.querySelector('.product-header > a');
    if (titleLink && titleLink.href) {
      var fullProduct = document.createElement('a');
      fullProduct.href = titleLink.href;
      fullProduct.textContent = 'View Full Product';
      fullProduct.addEventListener('click', function () {
        var current = root.querySelector('.product-header > a');
        if (current && current.href) fullProduct.href = current.href;
      });
      row.appendChild(fullProduct);
    }

    form.insertAdjacentElement('afterend', row);
  }

  /**
   * Desktop: quick-add.js crea la fila .lov-policy-links (Size Guide | Shipping
   * Policy) encima del botón. Cambiamos "Shipping Policy" por "View Full Product",
   * que apunta al mismo destino que el título del modal (.view-product-title a).
   * Si por alguna razón no hay enlace de producto, se deja como estaba.
   */
  function updateDesktopLinks() {
    var root = getContent();
    var row = root && root.querySelector('.lov-policy-links');
    if (!row) return;

    var target = null;
    row.querySelectorAll('a').forEach(function (link) {
      if ((link.getAttribute('href') || '').indexOf('shipping-policy') !== -1) target = link;
    });
    if (!target) return;

    var titleLink = root.querySelector('.view-product-title a');
    if (!titleLink || !titleLink.href) return;

    target.textContent = 'View Full Product';
    target.href = titleLink.href;
    target.removeAttribute('target');
    target.removeAttribute('rel');

    // El tema actualiza el enlace del título al cambiar de variante; lo releemos al tocar
    target.addEventListener('click', function () {
      var current = root.querySelector('.view-product-title a');
      if (current && current.href) target.href = current.href;
    });
  }

  /**
   * Estrellas de reviews.
   *
   * El bloque de estrellas (.rating-wrapper) viene dentro de .product-details,
   * que quick-add.js elimina en móvil. Por eso envolvemos updateQuickAddModal:
   * antes de que corra, guardamos una copia de las estrellas; después, las
   * ponemos en la misma fila que el precio (a la derecha), igual que en desktop.
   */
  function repairStarIds(rating) {
    // quick-add.js renombra los id="" del contenido (p. ej. el símbolo #star y el
    // degradado #half) pero no actualiza los <use href="#star">, así que los
    // reapuntamos al id nuevo para que las estrellas se dibujen siempre, aunque la
    // página que abrió el modal no tenga otras estrellas con el id original.
    var map = {};
    rating.querySelectorAll('[id]').forEach(function (el) {
      var base = el.id.replace(/-qa-[a-z0-9]+$/, '');
      if (base !== el.id) map[base] = el.id;
    });
    if (!Object.keys(map).length) return;

    rating.querySelectorAll('use').forEach(function (use) {
      ['href', 'xlink:href'].forEach(function (attr) {
        var value = use.getAttribute(attr);
        if (value && value.charAt(0) === '#' && map[value.slice(1)]) {
          use.setAttribute(attr, '#' + map[value.slice(1)]);
        }
      });

      var fill = use.getAttribute('fill');
      if (fill) {
        use.setAttribute(
          'fill',
          fill.replace(/url\(#([^)]+)\)/, function (match, id) {
            return map[id] ? 'url(#' + map[id] + ')' : match;
          })
        );
      }
    });
  }

  function placeRating(rating) {
    var root = getContent();
    if (!root) return;

    var header = root.querySelector('.product-header');
    var price = header && header.querySelector('product-price');
    if (!price) return;

    // Quita las estrellas de una apertura anterior, si quedaron
    var stale = header.querySelector('.rating-wrapper');
    if (stale) stale.remove();

    repairStarIds(rating);
    rating.classList.add('lov-rating-in-title');

    var row = price.parentElement;
    if (!row.classList.contains('lov-price-row')) {
      row = document.createElement('div');
      row.className = 'lov-price-row';
      header.insertBefore(row, price);
      row.appendChild(price);
    }
    row.appendChild(rating);
  }

  // Desktop: las estrellas ya vienen en .product-details; solo reparamos sus ids
  function repairDesktopStars() {
    var root = getContent();
    var rating = root && root.querySelector('.rating-wrapper');
    if (rating) repairStarIds(rating);
  }

  function patchQuickAdd() {
    import('@theme/quick-add')
      .then(function (mod) {
        var QuickAdd = mod && mod.QuickAddComponent;
        if (!QuickAdd || !QuickAdd.prototype || QuickAdd.prototype.__lovRatingPatched) return;

        var original = QuickAdd.prototype.updateQuickAddModal;
        if (typeof original !== 'function') return;

        QuickAdd.prototype.updateQuickAddModal = async function (productGrid) {
          var isMobile = MOBILE.matches;
          var rating = null;

          if (isMobile && productGrid && productGrid.querySelector) {
            var found =
              productGrid.querySelector('.product-details .rating-wrapper') ||
              productGrid.querySelector('.rating-wrapper');
            if (found) rating = found.cloneNode(true);
          }

          var result = await original.call(this, productGrid);

          // Cada ajuste va en su propio try/catch: si uno falla, el modal sigue
          // funcionando y los demás ajustes se aplican igual.
          if (isMobile) {
            try {
              if (rating) placeRating(rating);
            } catch (error) {}
          } else {
            try {
              repairDesktopStars();
            } catch (error) {}
            try {
              updateDesktopLinks();
            } catch (error) {}
          }

          return result;
        };

        QuickAdd.prototype.__lovRatingPatched = true;
      })
      .catch(function () {
        // Si algo falla aquí, el modal sigue funcionando, solo sin este ajuste
      });
  }

  function onOpen() {
    if (!MOBILE.matches) return;
    var root = getContent();
    if (!root) return;
    buildCarousel(root);
    setupWysh(root);
    setupDelivery(root);
    setupLinks(root);
  }

  function watchDialog() {
    var dialog = document.querySelector('#quick-add-dialog dialog');
    if (!dialog) return;

    var wasOpen = dialog.hasAttribute('open');
    new MutationObserver(function () {
      var isOpen = dialog.hasAttribute('open');
      if (isOpen && !wasOpen) onOpen();
      wasOpen = isOpen;
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  // Al cambiar de color dentro del modal, refiltra las fotos
  document.addEventListener('change', function (event) {
    if (!MOBILE.matches) return;
    var root = getContent();
    if (!root || !root.contains(event.target)) return;

    var target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    filterCarousel(root);
  });

  patchQuickAdd();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDialog);
  } else {
    watchDialog();
  }
})();
